import api from './api'
import { Lead } from '../types/lead'

export interface LeadStatusesResponse {
  default?: string[]
  custom?: string[]
}

export const mergeLeadStatuses = (res?: LeadStatusesResponse): string[] => {
  if (!res) return []
  return Array.from(new Set([...(res.default || []), ...(res.custom || [])]))
}

// Leads API
export const leadsApi = {
  getLeads: async (params?: {
    smbId?: string
    page?: number
    limit?: number
    status?: string
    source?: string
    search?: string
  }) => {
    const response = await api.get('/leads/', { params })
    return response.data
  },

  createLead: async (
    smbId: string,
    leadData: {
      name: string
      phone: string
      source?: string
      lead_status?: string
      notes?: string
      custom_fields?: Record<string, any>
    }
  ): Promise<{
    status: number
    data: Lead
  }> => {
    const response = await api.post(
      '/leads/',
      {
        name: leadData.name,
        phone: leadData.phone,
        source: leadData.source ?? 'direct',
        lead_status: leadData.lead_status,
        notes: leadData.notes,
        custom_fields: leadData.custom_fields,
      },
      {
        params: { smbId },
      }
    )
    return { status: response.status, data: response.data }
  },

  getLead: async (leadId: string): Promise<Lead> => {
    const response = await api.get(`/leads/${leadId}`)
    return response.data
  },

  updateLead: async (
    leadId: string,
    updates: {
      name?: string
      phone?: string
      source?: string
      lead_status?: string
      notes?: string
      custom_fields?: Record<string, any>
    }
  ): Promise<Lead> => {
    const response = await api.patch(`/leads/${leadId}`, updates)
    return response.data
  },

  deleteLead: async (leadId: string) => {
    const response = await api.delete(`/leads/${leadId}`)
    return response.data
  },

  getLeadsSummary: async () => {
    const response = await api.get('/leads/stats/summary')
    return response.data
  },

  getLeadStatuses: async (smbId: string): Promise<LeadStatusesResponse> => {
    const response = await api.get(`/smbs/${smbId}/lead-statuses`)
    return response.data
  },

  getAllLeadStatuses: async (smbId: string): Promise<string[]> => {
    const res = await leadsApi.getLeadStatuses(smbId)
    return mergeLeadStatuses(res)
  },

  addLeadStatus: async (smbId: string, status: string): Promise<{ message: string }> => {
    const response = await api.post(`/smbs/${smbId}/lead-statuses`, { status })
    return response.data
  },

  renameLeadStatus: async (
    smbId: string,
    oldStatus: string,
    newStatus: string
  ): Promise<{ message: string }> => {
    const response = await api.post(`/smbs/${smbId}/lead-statuses/rename`, {
      old_status: oldStatus,
      new_status: newStatus,
    })
    return response.data
  },
}
